import { useState } from 'react'
import { AskDialog, Button, Icon, Pill, toastError, toastSuccess } from 'admination-react'
import { captureWriteIdentity, isWriteIdentityCurrent } from '@/core/api/http'
import { useApp } from '@/core/state/appStore'
import { ModalHeader } from '@/core/components/modal/ModalHeader'
import { useVerbisLebenslaufUpload } from './useVerbisLebenslaufUpload'
import { UEBERSPRINGEN_TEXT, VerbisLebenslaufHochladen, VerbisLebenslaufUploadAction } from './VerbisLebenslaufHochladen'
import { VerbisLebenslaufFehlerPrompt } from './prompts'
import { classifyVerbisUeberspringenFailure } from './errors'
import { verbisUeberspringen } from '../../api/verbisLebenslauf'

export const UEBERSPRUNGEN_TOAST = 'Ohne VerBIS-Daten in den nächsten Status verschoben.'

export function VerbisLebenslauf({ coachingId }: { coachingId: string | null }) {
  const { dispatch, reloadDashboard, coachings } = useApp()
  const name = coachings.find((coaching) => coaching.id === coachingId)?.name ?? 'die Person'
  const upload = useVerbisLebenslaufUpload(coachingId)
  const [ueberspringenFragen, setUeberspringenFragen] = useState(false)
  const [ueberspringt, setUeberspringt] = useState(false)

  const darfUeberspringen = !upload.statusError && (!(upload.done.verbis && upload.done.lebenslauf) || upload.hinweise.length > 0 || upload.ohneDatum.length > 0)

  async function ueberspringen() {
    if (!coachingId || ueberspringt) return
    const identity = captureWriteIdentity()
    setUeberspringt(true)
    try {
      await verbisUeberspringen(coachingId)
      if (!isWriteIdentityCurrent(identity)) return
      toastSuccess(upload.fuerPerson(coachingId, UEBERSPRUNGEN_TOAST))
      setUeberspringenFragen(false)
      dispatch({ type: 'CLOSE_FOR', coachingId })
      reloadDashboard()
    } catch (e) {
      if (!isWriteIdentityCurrent(identity)) return
      console.error('VerBIS überspringen fehlgeschlagen:', e)
      const failure = classifyVerbisUeberspringenFailure(e)
      toastError(upload.fuerPerson(coachingId, failure.message))
      setUeberspringenFragen(false)
      if (failure.reload) {
        reloadDashboard()
        upload.reloadStatus()
      }
    } finally {
      if (isWriteIdentityCurrent(identity)) setUeberspringt(false)
    }
  }

  return (
    <>
      <ModalHeader tabs={{ hochladen: { completed: upload.done.verbis && upload.done.lebenslauf } }} onClose={() => dispatch({ type: 'CLOSE' })}>
        {darfUeberspringen && <Pill><Button disabled={upload.uploading || upload.statusLoading} onClick={() => setUeberspringenFragen(true)}><Icon name="i-arrow-right" />{UEBERSPRINGEN_TEXT}</Button></Pill>}
        <VerbisLebenslaufUploadAction upload={upload} />
      </ModalHeader>
      <VerbisLebenslaufHochladen
        done={upload.done}
        statusLoading={upload.statusLoading}
        statusError={upload.statusError}
        uploading={upload.uploading}
        eintraege={upload.eintraege}
        hinweise={upload.hinweise}
        ohneDatum={upload.ohneDatum}
        onSaveZeitraum={upload.saveZeitraum}
        onAdd={upload.add}
        onRemove={upload.remove}
      />
      {upload.fehlerPrompt && (
        <VerbisLebenslaufFehlerPrompt
          meldung={upload.fehlerPrompt.meldung}
          fehler={upload.fehlerPrompt.fehler}
          onClose={upload.closeFehlerPrompt}
        />
      )}
      {ueberspringenFragen && (
        <AskDialog
          tone="warning"
          title={`Sicher, dass Sie ${name} in den nächsten Status verschieben möchten?`}
          headingLevel="h2"
          cancelLabel="Nein"
          confirmLabel="Ja"
          busy={ueberspringt}
          onDecide={(ja) => (ja ? void ueberspringen() : setUeberspringenFragen(false))}
        >
          Es liegen keine VerBIS-Daten vor. Die Person kommt nach „Teilnehmerakte & Erstgespräch“ und bekommt ihr
          Passwort. VerBIS-Screenshots und Lebenslauf können Sie später in der Übersicht der Person hochladen.
        </AskDialog>
      )}
    </>
  )
}
